import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { requestJson } from "@/lib/api-client";
import type {
  MenuCollection,
  MenuCollectionPromotion,
  MenuCollectionPromotionsResponse,
  MenuCollectionsResponse,
  MenuCustomPromotion,
  MenuCustomPromotionsResponse,
  MenuProduct,
  MenuProductUpdateInput,
  MenuProductsResponse,
  MenuSection,
  MenuSectionPromotion,
  MenuSectionPromotionsResponse,
  MenuSectionsResponse,
  ProductsViewResponse,
  PublicMenuResponse,
  UpsertCollectionPromotionInput,
  UpsertCustomPromotionInput,
  UpsertMenuCollectionInput,
  UpsertMenuProductInput,
  UpsertMenuSectionInput,
  UpsertSectionPromotionInput,
} from "@/types/menu";
import { queryKeys } from "@/lib/query-keys";

const JSON_HEADERS = {
  "Content-Type": "application/json",
} as const;

export function useMenuSections(activeOrganization?: string) {
  return useQuery({
    queryKey: queryKeys.menu.sections.byOrg(activeOrganization),
    enabled: !!activeOrganization,
    queryFn: async () => {
      return await requestJson<MenuSectionsResponse>("/api/menu/sections");
    },
  });
}

export function useCreateMenuSection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpsertMenuSectionInput) => {
      const response = await requestJson<{ section: MenuSection }>("/api/menu/sections", {
        body: JSON.stringify(data),
        headers: JSON_HEADERS,
        method: "POST",
      });

      return response.section;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.menu.sections.all() });
    },
  });
}

export function useUpdateMenuSection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<UpsertMenuSectionInput> }) => {
      const response = await requestJson<{ section: MenuSection }>(`/api/menu/sections/${id}`, {
        body: JSON.stringify(data),
        headers: JSON_HEADERS,
        method: "PATCH",
      });

      return response.section;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.menu.sections.all() });
    },
  });
}

export function useDeleteMenuSection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return await requestJson<{ id: string; success: boolean }>(`/api/menu/sections/${id}`, {
        method: "DELETE",
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.menu.sections.all() });
    },
  });
}

// Collections hooks

export function useMenuCollections(activeOrganization?: string) {
  return useQuery({
    queryKey: queryKeys.menu.collections.byOrg(activeOrganization),
    enabled: !!activeOrganization,
    queryFn: async () => {
      return await requestJson<MenuCollectionsResponse>("/api/menu/collections");
    },
  });
}

export function useCreateMenuCollection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpsertMenuCollectionInput) => {
      const response = await requestJson<{ collection: MenuCollection }>("/api/menu/collections", {
        body: JSON.stringify(data),
        headers: JSON_HEADERS,
        method: "POST",
      });

      return response.collection;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.menu.collections.all(),
      });
    },
  });
}

export function useUpdateMenuCollection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<UpsertMenuCollectionInput> }) => {
      const response = await requestJson<{ collection: MenuCollection }>(
        `/api/menu/collections/${id}`,
        {
          body: JSON.stringify(data),
          headers: JSON_HEADERS,
          method: "PATCH",
        },
      );

      return response.collection;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.menu.collections.all(),
      });
    },
  });
}

export function useDeleteMenuCollection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return await requestJson<{ id: string; success: boolean }>(`/api/menu/collections/${id}`, {
        method: "DELETE",
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.menu.collections.all(),
      });
    },
  });
}

// Products hooks

export function useMenuProducts(activeOrganization?: string) {
  return useQuery({
    queryKey: queryKeys.menu.products.byOrg(activeOrganization),
    enabled: !!activeOrganization,
    queryFn: async () => {
      return await requestJson<MenuProductsResponse>("/api/menu/products");
    },
  });
}

export function useCreateMenuProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpsertMenuProductInput) => {
      const response = await requestJson<{ product: MenuProduct }>("/api/menu/products", {
        body: JSON.stringify(data),
        headers: JSON_HEADERS,
        method: "POST",
      });

      return response.product;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.menu.products.all(),
      });
    },
  });
}

export function useUpdateMenuProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: MenuProductUpdateInput }) => {
      const response = await requestJson<{ product: MenuProduct }>(`/api/menu/products/${id}`, {
        body: JSON.stringify(data),
        headers: JSON_HEADERS,
        method: "PATCH",
      });

      return response.product;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.menu.products.all(),
      });
    },
  });
}

export function useDeleteMenuProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return await requestJson<{ id: string; success: boolean }>(`/api/menu/products/${id}`, {
        method: "DELETE",
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.menu.products.all(),
      });
    },
  });
}

// Custom Promotions hooks

export function useCustomPromotions(activeOrganization?: string) {
  return useQuery({
    queryKey: queryKeys.menu.promotions.custom.byOrg(activeOrganization),
    enabled: !!activeOrganization,
    queryFn: async () => {
      return await requestJson<MenuCustomPromotionsResponse>("/api/menu/promotions/custom");
    },
  });
}

export function useCreateCustomPromotion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpsertCustomPromotionInput) => {
      const response = await requestJson<{
        promotion: MenuCustomPromotion;
      }>("/api/menu/promotions/custom", {
        body: JSON.stringify(data),
        headers: JSON_HEADERS,
        method: "POST",
      });

      return response.promotion;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.menu.promotions.custom.all(),
      });
    },
  });
}

export function useUpdateCustomPromotion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<UpsertCustomPromotionInput> }) => {
      const response = await requestJson<{
        promotion: MenuCustomPromotion;
      }>(`/api/menu/promotions/custom/${id}`, {
        body: JSON.stringify(data),
        headers: JSON_HEADERS,
        method: "PATCH",
      });

      return response.promotion;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.menu.promotions.custom.all(),
      });
    },
  });
}

export function useDeleteCustomPromotion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return await requestJson<{ id: string; success: boolean }>(
        `/api/menu/promotions/custom/${id}`,
        {
          method: "DELETE",
        },
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.menu.promotions.custom.all(),
      });
    },
  });
}

// Section Promotions hooks

export function useSectionPromotions(activeOrganization?: string) {
  return useQuery({
    queryKey: queryKeys.menu.promotions.section.byOrg(activeOrganization),
    enabled: !!activeOrganization,
    queryFn: async () => {
      return await requestJson<MenuSectionPromotionsResponse>("/api/menu/promotions/section");
    },
  });
}

export function useCreateSectionPromotion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpsertSectionPromotionInput) => {
      const response = await requestJson<{
        promotion: MenuSectionPromotion;
      }>("/api/menu/promotions/section", {
        body: JSON.stringify(data),
        headers: JSON_HEADERS,
        method: "POST",
      });

      return response.promotion;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.menu.promotions.section.all(),
      });
    },
  });
}

export function useUpdateSectionPromotion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Partial<UpsertSectionPromotionInput>;
    }) => {
      const response = await requestJson<{
        promotion: MenuSectionPromotion;
      }>(`/api/menu/promotions/section/${id}`, {
        body: JSON.stringify(data),
        headers: JSON_HEADERS,
        method: "PATCH",
      });

      return response.promotion;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.menu.promotions.section.all(),
      });
    },
  });
}

export function useDeleteSectionPromotion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return await requestJson<{ id: string; success: boolean }>(
        `/api/menu/promotions/section/${id}`,
        {
          method: "DELETE",
        },
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.menu.promotions.section.all(),
      });
    },
  });
}

// Collection Promotions hooks

export function useCollectionPromotions(activeOrganization?: string) {
  return useQuery({
    queryKey: queryKeys.menu.promotions.collection.byOrg(activeOrganization),
    enabled: !!activeOrganization,
    queryFn: async () => {
      return await requestJson<MenuCollectionPromotionsResponse>("/api/menu/promotions/collection");
    },
  });
}

export function useCreateCollectionPromotion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpsertCollectionPromotionInput) => {
      const response = await requestJson<{
        promotion: MenuCollectionPromotion;
      }>("/api/menu/promotions/collection", {
        body: JSON.stringify(data),
        headers: JSON_HEADERS,
        method: "POST",
      });

      return response.promotion;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.menu.promotions.collection.all(),
      });
    },
  });
}

export function useUpdateCollectionPromotion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Partial<UpsertCollectionPromotionInput>;
    }) => {
      const response = await requestJson<{
        promotion: MenuCollectionPromotion;
      }>(`/api/menu/promotions/collection/${id}`, {
        body: JSON.stringify(data),
        headers: JSON_HEADERS,
        method: "PATCH",
      });

      return response.promotion;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.menu.promotions.collection.all(),
      });
    },
  });
}

export function useDeleteCollectionPromotion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return await requestJson<{ id: string; success: boolean }>(
        `/api/menu/promotions/collection/${id}`,
        {
          method: "DELETE",
        },
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.menu.promotions.collection.all(),
      });
    },
  });
}

// Products View (read-only)

export function useProductsWithBestPromotion(activeOrganization?: string) {
  return useQuery({
    queryKey: queryKeys.menu.productsView.byOrg(activeOrganization),
    enabled: !!activeOrganization,
    queryFn: async () => {
      return await requestJson<ProductsViewResponse>("/api/menu/products-view");
    },
  });
}

export function usePublicMenu(orgSlug?: string) {
  return useQuery({
    queryKey: queryKeys.menu.publicMenu.byOrgSlug(orgSlug),
    enabled: !!orgSlug,
    queryFn: async () => {
      return await requestJson<PublicMenuResponse>(`/api/menu/public/${orgSlug}`);
    },
  });
}
